import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Target, ChevronDown, Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

let cache = null;
export const clearKpiOptions = () => { cache = null; };
const loadOptions = () => {
  if (!cache) cache = Promise.all([client.get("/kpis"), client.get("/kpi-categories")]).then(([k, c]) => ({ kpis: k.data, cats: c.data })).catch((e) => { cache = null; throw e; });
  return cache;
};

// Dropdown multi-select of the assigned people's KPIs, grouped by Focus Area; hidden when they have none.
export function KPIPicker({ value = [], onChange, userIds = [], testid = "kpi-picker" }) {
  const [opts, setOpts] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { loadOptions().then(setOpts).catch(() => setOpts({ kpis: [], cats: [] })); }, []);
  if (!opts) return null;
  const ids = userIds.filter(Boolean);
  const kpis = opts.kpis.filter((k) => value.includes(k.id) || (k.user_ids || []).some((u) => ids.includes(u)));
  if (!kpis.length) return null;
  const groups = [...opts.cats.map((c) => [c.name, kpis.filter((k) => k.category_id === c.id)]), ["Other", kpis.filter((k) => !opts.cats.some((c) => c.id === k.category_id))]].filter(([, ks]) => ks.length);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const selected = kpis.filter((k) => value.includes(k.id));
  return (
    <div data-testid={testid}>
      <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Target className="h-3.5 w-3.5 text-sky-600" />KPIs <span className="font-normal text-slate-400">(this counts towards)</span></label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="flex min-h-9 w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-left text-sm transition-colors hover:border-slate-300 focus:border-sky-400 focus:outline-none" data-testid={`${testid}-trigger`}>
            <div className="flex flex-1 flex-wrap gap-1">
              {selected.length ? selected.map((k) => (
                <span key={k.id} className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700" data-testid={`${testid}-tag-${k.id}`}>
                  {k.name}
                  <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); toggle(k.id); }} className="hover:text-sky-900"><X className="h-3 w-3" /></span>
                </span>
              )) : <span className="px-0.5 text-slate-400">Choose KPIs…</span>}
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="max-h-72 w-[var(--radix-popover-trigger-width)] overflow-y-auto p-1" data-testid={`${testid}-menu`}>
          {groups.map(([name, ks]) => (
            <div key={name} className="py-0.5">
              <p className="px-2 pb-0.5 pt-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{name}</p>
              {ks.map((k) => {
                const on = value.includes(k.id);
                return (
                  <button type="button" key={k.id} onClick={() => toggle(k.id)} title={k.description || k.name} data-testid={`${testid}-opt-${k.id}`}
                    className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-slate-50", on ? "text-sky-700" : "text-slate-700")}>
                    <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded border", on ? "border-sky-500 bg-sky-500 text-white" : "border-slate-300")}>{on && <Check className="h-3 w-3" />}</span>
                    <span className="truncate">{k.name}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
